import React from "react";

import {
  faArrowDownToLine,
  faClone,
  faDatabase,
  faStop,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { JobRun } from "src/jobs/types";
import { isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  canSimulationBeDeleted,
  isSimulationComplete,
  isSimulationInProgress,
  simulationCanBeStopped,
} from "src/simulations/simulationUtils";

type SimulationOptionsDropdownProps = {
  simulation: JobRun;
  onDelete: () => void;
  onCreateFailedDataset: () => void;
  onDownloadCsv: () => void;
  onDownloadNdjson: () => void;
  onCopyCsvUrl: () => void;
  onCopyNdjsonUrl: () => void;
} & (
  | { includeStopOption: true; onStop: () => void }
  | { includeStopOption: false; onStop?: never }
);

export const SimulationOptionsDropdown: React.FC<
  SimulationOptionsDropdownProps
> = ({
  simulation,
  includeStopOption: stoppingAllowed,
  onDelete,
  onCreateFailedDataset,
  onDownloadCsv,
  onDownloadNdjson,
  onCopyCsvUrl,
  onCopyNdjsonUrl,
  onStop,
}) => {
  const isCompleted = isSimulationComplete(simulation);
  const hasSuccessfulRows = simulation.success_rows_count > 0;
  const hasFailedRows = simulation.failed_rows_count > 0;
  const showDeleteOption = canSimulationBeDeleted(simulation);
  const showStopOption = stoppingAllowed && isSimulationInProgress(simulation);
  const stopDisabled = !simulationCanBeStopped(simulation);

  const optionsElements: OptionsDropdownElement[] = [];
  if (isCompleted && hasSuccessfulRows) {
    if (isFeatureFlagEnabled("notebooks")) {
      optionsElements.push({
        key: "Copy CSV URL",
        icon: faClone,
        action: onCopyCsvUrl,
      });
      optionsElements.push({
        key: "Copy NDJSON URL",
        icon: faClone,
        action: onCopyNdjsonUrl,
      });
    }
    optionsElements.push({
      key: "Download as CSV",
      icon: faArrowDownToLine,
      action: onDownloadCsv,
    });
    optionsElements.push({
      key: "Download as NDJSON",
      icon: faArrowDownToLine,
      action: onDownloadNdjson,
    });
  }
  if (isCompleted && hasFailedRows) {
    optionsElements.push({
      key: "Convert failed rows to a test dataset",
      icon: faDatabase,
      action: onCreateFailedDataset,
    });
  }

  if (optionsElements.length > 0) {
    optionsElements.push(MENU_DIVIDER);
  }
  if (showStopOption && onStop) {
    optionsElements.push({
      key: "Stop simulation",
      icon: faStop,
      action: onStop,
      disabled: stopDisabled
        ? "This simulation can only be stopped while decisions are being processed"
        : false,
    });
  }
  if (showDeleteOption) {
    optionsElements.push({
      key: "Delete simulation",
      icon: faTrashCan,
      action: onDelete,
    });
  }

  return (
    <EllipsisOptionsDropdown
      dataLoc="simulation-actions"
      elements={optionsElements}
      ellipsisDirection="vertical"
      timeoutDuration={200}
    />
  );
};
